import React, { useState } from 'react'
import { Grid, InputLabel, TextField, Box, Button, Tooltip, Card, CardContent, Typography, Chip, Divider, Paper, IconButton } from '@mui/material'
import { styled } from '@mui/material/styles'
import { artworkGuidlines, releaseType, staticGenre } from '../../../constants'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from "moment"
import AddIcon from '@mui/icons-material/Add';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import SelectCmp from "react-select"
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import CustomShowModal from "../../../components/Common/CustomShowModal"
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import AlbumIcon from '@mui/icons-material/Album';

const selectStyles = {
    control: (base, state) => ({
        ...base,
        background: '#ffffff',
        borderRadius: '8px',
        borderColor: state.isFocused ? '#1a73e8' : 'rgba(0, 0, 0, 0.12)',
        borderWidth: state.isFocused ? '2px' : '1px',
        boxShadow: 'none',
        '&:hover': {
            borderColor: 'rgba(26, 115, 232, 0.3)',
        },
    }),
    singleValue: (base) => ({
        ...base,
        color: '#202124',
        fontFamily: `"DM Sans", sans-serif`
    }),
    placeholder: (base) => ({
        ...base,
        color: '#5f6368',
        fontFamily: `"DM Sans", sans-serif`
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#1a73e8' : state.isFocused ? 'rgba(26, 115, 232, 0.08)' : '#ffffff',
        color: state.isSelected ? '#ffffff' : '#202124',
        fontFamily: `"DM Sans", sans-serif`,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(26, 115, 232, 0.08)',
        },
    }),
    input: (base) => ({
        ...base,
        color: '#202124',
    }),
}


const StyledButton = styled(Button)(({ theme }) => ({
    fontFamily: "'DM Sans', sans-serif",
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    fontSize: '1rem',
    fontWeight: 600,
    height: 48,
    minWidth: 160,
    borderRadius: '12px',
    color: "white",
    textTransform: 'none',
    boxShadow: '0 4px 12px rgba(26, 115, 232, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
        boxShadow: '0 6px 20px rgba(26, 115, 232, 0.4)',
        transform: 'translateY(-2px)',
    },
    '&:active': {
        transform: 'translateY(0)',
    }
}));

const CssTextField = styled(TextField)(({ theme }) => ({
    fontFamily: "'DM Sans', sans-serif",
    '& .MuiOutlinedInput-root': {
        background: '#ffffff',
        borderRadius: '8px',
        transition: 'all 0.3s ease',
        '& fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.12)',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(26, 115, 232, 0.3)',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#1a73e8',
            borderWidth: '2px',
        },
    },
    '& .MuiOutlinedInput-input': {
        color: '#202124',
        fontFamily: "'DM Sans', sans-serif",
        padding: '8.5px 14px',
    },
    '& .MuiInputLabel-root': {
        color: '#5f6368',
        fontFamily: "'DM Sans', sans-serif",
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: '#1a73e8',
    },
}));

const SectionCard = styled(Card)({
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
    },
});

const ArtworkSectionCard = styled(Card)({
    background: 'linear-gradient(135deg, rgba(26, 115, 232, 0.05) 0%, rgba(0, 191, 165, 0.05) 100%)',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        transform: 'translateY(-2px)',
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50px',
        right: '-50px',
        width: '200px',
        height: '200px',
        background: 'radial-gradient(circle, rgba(26, 115, 232, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '-30px',
        left: '-30px',
        width: '150px',
        height: '150px',
        background: 'radial-gradient(circle, rgba(0, 191, 165, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
    },
});

const ReleaseTypeChip = styled(Box)(({ active }) => ({
    padding: '12px 20px',
    border: active ? 'none' : '2px solid rgba(0, 0, 0, 0.12)',
    background: active ? 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)' : '#ffffff',
    color: active ? '#ffffff' : '#5f6368',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.95rem',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    boxShadow: active ? '0 4px 12px rgba(26, 115, 232, 0.3)' : 'none',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: active ? '0 6px 16px rgba(26, 115, 232, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
        borderColor: active ? 'transparent' : 'rgba(26, 115, 232, 0.5)',
    },
}));

const ArtworkUploadBox = styled(Box)({
    width: '100%',
    maxWidth: '280px',
    height: '280px',
    margin: 'auto',
    borderRadius: '16px',
    border: '3px dashed rgba(26, 115, 232, 0.3)',
    background: 'linear-gradient(135deg, rgba(26, 115, 232, 0.05) 0%, rgba(0, 191, 165, 0.05) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
        borderColor: '#1a73e8',
        background: 'linear-gradient(135deg, rgba(26, 115, 232, 0.1) 0%, rgba(0, 191, 165, 0.1) 100%)',
        transform: 'scale(1.02)',
    },
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: '13px',
    },
});

const ChangeImageButton = styled(Button)({
    marginTop: '12px',
    background: 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)',
    color: 'white',
    padding: '8px 24px',
    borderRadius: '8px',
    fontWeight: 600,
    textTransform: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(239, 83, 80, 0.3)',
    },
});

const StyledInputLabel = styled(InputLabel)({
    fontWeight: 600,
    fontSize: '0.95rem',
    color: '#202124',
    marginBottom: '8px',
    fontFamily: "'DM Sans', sans-serif",
});


function ReleaseInfo(props) {

    const {
        releaseInfo,
        setReleaseInfo,
        recipeFilePreview,
        onFileHandler,
        primaryArtist,
        featuringArtist,
        setOpenAddPrimary,
        setOpenAddFeaturing,
        setAddArtist,
        addArtist,
        editMode,
        createReleaseInfo,
        removeSelectedPrimary,
        onChangeSelect,
        selectedPrimaryArtist = [],
        selectedFeaturingArtist = [],
        releaseDateError = '',
        setReleaseDateError
    } = props;

    const [open, setOpen] = useState(false)

    const selectedGenre = staticGenre.find(item => item.value === releaseInfo.genre)

    return (
        <Grid container spacing={3} className="releaseinfo-container">
            {/* Left Column - Artwork */}
            <Grid item xs={12} md={4}>
                <ArtworkSectionCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Decorative Background Icons */}
                    <MusicNoteIcon sx={{
                        position: 'absolute',
                        top: '20px',
                        left: '20px',
                        fontSize: '3rem',
                        color: 'rgba(26, 115, 232, 0.08)',
                        transform: 'rotate(-15deg)',
                        zIndex: 0
                    }} />
                    <AudiotrackIcon sx={{
                        position: 'absolute',
                        bottom: '80px',
                        right: '30px',
                        fontSize: '2.5rem',
                        color: 'rgba(0, 191, 165, 0.08)',
                        transform: 'rotate(20deg)',
                        zIndex: 0
                    }} />
                    <AlbumIcon sx={{
                        position: 'absolute',
                        top: '120px',
                        right: '15px',
                        fontSize: '2rem',
                        color: 'rgba(26, 115, 232, 0.06)',
                        transform: 'rotate(-10deg)',
                        zIndex: 0
                    }} />
                    <MusicNoteIcon sx={{
                        position: 'absolute',
                        bottom: '150px',
                        left: '15px',
                        fontSize: '2rem',
                        color: 'rgba(0, 191, 165, 0.06)',
                        transform: 'rotate(25deg)',
                        zIndex: 0
                    }} />

                    <Box textAlign="center" sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#202124' }}>
                            Album Artwork
                        </Typography>
                        <label htmlFor="releaseInfo-file-upload" style={{ cursor: 'pointer' }}>
                            <ArtworkUploadBox>
                                {recipeFilePreview ? (
                                    <img src={recipeFilePreview} alt="Album artwork" />
                                ) : (
                                    <Box textAlign="center">
                                        <WallpaperIcon sx={{ fontSize: '4rem', color: '#1a73e8', mb: 1 }} />
                                        <Typography variant="body1" sx={{ color: '#5f6368', fontWeight: 600 }}>
                                            Upload Album Artwork
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#9aa0a6', mt: 1, display: 'block' }}>
                                            Click to browse
                                        </Typography>
                                    </Box>
                                )}
                            </ArtworkUploadBox>
                        </label>
                        {recipeFilePreview && (
                            <label htmlFor="releaseInfo-file-upload">
                                <ChangeImageButton>
                                    Change Image
                                </ChangeImageButton>
                            </label>
                        )}
                        <input
                            type="file"
                            id="releaseInfo-file-upload"
                            accept="image/x-png,image/gif,image/jpeg"
                            onChange={onFileHandler}
                            style={{ display: 'none' }}
                        />
                        <Button
                            startIcon={<InfoOutlinedIcon />}
                            onClick={() => setOpen(true)}
                            sx={{
                                mt: 2,
                                color: '#1a73e8',
                                fontWeight: 600,
                                textTransform: 'none',
                                position: 'relative',
                                zIndex: 1,
                                '&:hover': { background: 'rgba(26, 115, 232, 0.08)' }
                            }}
                        >
                            View Guidelines
                        </Button>
                    </Box>
                </ArtworkSectionCard>
            </Grid>

            {/* Right Column - Form Fields */}
            <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                    {/* Release Type */}
                    <Grid item xs={12}>
                        <SectionCard>
                            <StyledInputLabel htmlFor="ReleaseType">
                                Release Type<span className="required-span">*</span>
                            </StyledInputLabel>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                                {releaseType.map((item, key) => {
                                    const isActive = item.label.toLowerCase() === releaseInfo.release_type
                                    return (
                                        <ReleaseTypeChip
                                            key={key}
                                            active={isActive}
                                            onClick={() => setReleaseInfo({ ...releaseInfo, release_type: item.label.toLowerCase() })}
                                        >
                                            {item.label}
                                        </ReleaseTypeChip>
                                    )
                                })}
                            </Box>
                        </SectionCard>
                    </Grid>

                    {/* Release Title & Label Name */}
                    <Grid item xs={12} md={6}>
                        <StyledInputLabel htmlFor="ReleaseTitle">
                            Release Title<span className="required-span">*</span>
                        </StyledInputLabel>
                        <CssTextField
                            id="ReleaseTitle"
                            variant="outlined"
                            fullWidth
                            placeholder="Enter release title"
                            size="small"
                            value={releaseInfo.release_title}
                            onChange={(e) => setReleaseInfo({ ...releaseInfo, release_title: e.target.value })}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <StyledInputLabel htmlFor="LabelName">Label Name<span className="required-span">*</span></StyledInputLabel>
                        <CssTextField
                            id="LabelName"
                            variant="outlined"
                            fullWidth
                            placeholder="Enter label name"
                            size="small"
                            value={releaseInfo.lable_name}
                            onChange={(e) => setReleaseInfo({ ...releaseInfo, lable_name: e.target.value })}
                        />
                    </Grid>

                    {/* Artist Selection - Primary & Featuring in one row */}
                    <Grid item xs={12} md={6}>
                        <StyledInputLabel htmlFor="ReleaseArtist">Primary Artist<span className="required-span">*</span></StyledInputLabel>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Box sx={{ flex: 1 }}>
                                <SelectCmp
                                    styles={selectStyles}
                                    options={primaryArtist}
                                    placeholder="Select Primary Artist"
                                    onChange={(e) => onChangeSelect(e, "primary")}
                                />
                            </Box>
                            <IconButton
                                onClick={() => { setAddArtist({ ...addArtist, 'artist_type': "primary" }); setOpenAddPrimary(true) }}
                                sx={{
                                    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                                    color: '#ffffff',
                                    borderRadius: '8px',
                                    width: '40px',
                                    height: '40px',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
                                    }
                                }}
                            >
                                <AddIcon />
                            </IconButton>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <StyledInputLabel htmlFor="Featuring">Featuring Artist</StyledInputLabel>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Box sx={{ flex: 1 }}>
                                <SelectCmp
                                    styles={selectStyles}
                                    options={featuringArtist}
                                    placeholder="Select Featuring Artist"
                                    onChange={(e) => onChangeSelect(e, "")}
                                />
                            </Box>
                            <IconButton
                                onClick={() => { setAddArtist({ ...addArtist, 'artist_type': "featuring" }); setOpenAddFeaturing(true) }}
                                sx={{
                                    background: 'linear-gradient(135deg, #00bfa5 0%, #1a73e8 100%)',
                                    color: '#ffffff',
                                    borderRadius: '8px',
                                    width: '40px',
                                    height: '40px',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #009688 0%, #1557b0 100%)',
                                    }
                                }}
                            >
                                <AddIcon />
                            </IconButton>
                        </Box>
                    </Grid>

                    {/* Selected Artists Display */}
                    {(selectedPrimaryArtist.length > 0 || selectedFeaturingArtist.length > 0) && (
                        <Grid item xs={12}>
                            <SectionCard>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202124', mb: 2 }}>
                                    Selected Artists
                                </Typography>
                                <Grid container spacing={2}>
                                    {selectedPrimaryArtist.length > 0 && (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="caption" sx={{ color: '#1a73e8', fontWeight: 600, mb: 1, display: 'block' }}>
                                                Primary Artists
                                            </Typography>
                                            {selectedPrimaryArtist.map((item, key) => (
                                                <Box
                                                    key={key}
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        p: 1.5,
                                                        mb: 1,
                                                        background: 'linear-gradient(135deg, rgba(26, 115, 232, 0.05) 0%, rgba(0, 191, 165, 0.05) 100%)',
                                                        borderRadius: '8px',
                                                        border: '1px solid rgba(26, 115, 232, 0.2)',
                                                    }}
                                                >
                                                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#202124' }}>
                                                        {item?.artist_name || ''}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeSelectedPrimary("primary", key)}
                                                        sx={{ color: '#ef5350' }}
                                                    >
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Grid>
                                    )}
                                    {selectedFeaturingArtist.length > 0 && (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="caption" sx={{ color: '#00bfa5', fontWeight: 600, mb: 1, display: 'block' }}>
                                                Featuring Artists
                                            </Typography>
                                            {selectedFeaturingArtist.map((item, key) => (
                                                <Box
                                                    key={key}
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        p: 1.5,
                                                        mb: 1,
                                                        background: 'linear-gradient(135deg, rgba(0, 191, 165, 0.05) 0%, rgba(26, 115, 232, 0.05) 100%)',
                                                        borderRadius: '8px',
                                                        border: '1px solid rgba(0, 191, 165, 0.2)',
                                                    }}
                                                >
                                                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#202124' }}>
                                                        {item?.artist_name || ''}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeSelectedPrimary("", key)}
                                                        sx={{ color: '#ef5350' }}
                                                    >
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Grid>
                                    )}
                                </Grid>
                            </SectionCard>
                        </Grid>
                    )}

                    {/* Genre & Subgenre */}
                    <Grid item xs={12} md={6}>
                        <StyledInputLabel htmlFor="Genre">Genre<span className="required-span">*</span></StyledInputLabel>
                        <SelectCmp
                            styles={selectStyles}
                            options={staticGenre}
                            value={selectedGenre}
                            placeholder="Select Genre"
                            onChange={e => setReleaseInfo({ ...releaseInfo, genre: e.value })}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <StyledInputLabel htmlFor="Subgenre">Sub Genre<span className="required-span">*</span></StyledInputLabel>
                        <CssTextField
                            id="Subgenre"
                            variant="outlined"
                            fullWidth
                            placeholder="Enter sub genre"
                            size="small"
                            value={releaseInfo.sub_genre}
                            onChange={(e) => setReleaseInfo({ ...releaseInfo, sub_genre: e.target.value })}
                        />
                    </Grid>

                    {/* Release Date & Original Release Date */}
                    <Grid item xs={12} md={6}>
                        <StyledInputLabel htmlFor="Releasedate">Release Date<span className="required-span">*</span></StyledInputLabel>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                format="DD/MM/YYYY"
                                minDate={moment().add(6, 'd').startOf('day')}
                                value={releaseInfo.release_date ? moment(releaseInfo.release_date) : null}
                                onChange={date => {
                                    setReleaseInfo({ ...releaseInfo, release_date: date && date.valueOf() || null })
                                    if (date && date.valueOf()) {
                                        setReleaseDateError('')
                                    }
                                }}
                                onError={(error) => {
                                    if (error) {
                                        setReleaseDateError('Please select a valid date (minimum 6 days from today).')
                                    }
                                }}
                                enableAccessibleFieldDOMStructure={false}
                                slots={{
                                    textField: (params) => (
                                        <CssTextField
                                            {...params}
                                            size="small"
                                            fullWidth
                                            placeholder="Select release date"
                                            error={!!releaseDateError}
                                            helperText={releaseDateError}
                                            inputProps={{
                                                ...params.inputProps,
                                            }}
                                            InputProps={{
                                                ...params.InputProps,
                                            }}
                                        />
                                    )
                                }}
                                slotProps={{
                                    openPickerIcon: {
                                        sx: {
                                            color: '#1a73e8',
                                        }
                                    },
                                    popper: {
                                        sx: {
                                            '& .MuiPaper-root': {
                                                borderRadius: '12px',
                                                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.3)',
                                                background: '#e8eef3',
                                                border: '1px solid rgba(0, 0, 0, 0.2)',
                                            },
                                            '& .MuiPickersDay-root': {
                                                color: '#202124',
                                                fontWeight: 500,
                                                '&:hover': {
                                                    background: 'rgba(26, 115, 232, 0.2)',
                                                },
                                                '&.Mui-selected': {
                                                    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                                                    color: '#ffffff',
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
                                                    }
                                                },
                                                '&.Mui-disabled': {
                                                    color: '#ef5350 !important',
                                                    fontWeight: 500,
                                                    textDecoration: 'line-through',
                                                    opacity: 1,
                                                }
                                            },
                                            '& .MuiPickersDay-root.Mui-disabled': {
                                                color: '#ef5350 !important',
                                                fontWeight: 600,
                                                textDecoration: 'line-through',
                                            },
                                            '& button.MuiPickersDay-root.Mui-disabled': {
                                                color: '#ef5350 !important',
                                                fontWeight: 600,
                                            },
                                            '& .MuiPickersDay-root[disabled]': {
                                                color: '#ef5350 !important',
                                                fontWeight: 600,
                                            },
                                            '& .MuiDayCalendar-weekDayLabel.Mui-disabled': {
                                                color: '#ef5350 !important',
                                            },
                                            '& .MuiPickersDay-dayOutsideMonth': {
                                                color: '#ef5350 !important',
                                            },
                                            '& .MuiPickersYear-yearButton': {
                                                color: '#202124',
                                                fontWeight: 500,
                                                fontSize: '1rem',
                                                '&:hover': {
                                                    background: 'rgba(26, 115, 232, 0.2)',
                                                },
                                                '&.Mui-selected': {
                                                    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                                                    color: '#ffffff',
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
                                                    }
                                                },
                                            },
                                            '& .MuiYearCalendar-root': {
                                                color: '#202124',
                                            },
                                            '& .MuiPickersCalendarHeader-root': {
                                                color: '#202124',
                                                background: 'linear-gradient(135deg, rgba(26, 115, 232, 0.15) 0%, rgba(0, 191, 165, 0.15) 100%)',
                                                padding: '16px',
                                                marginBottom: '8px',
                                            },
                                            '& .MuiPickersCalendarHeader-label': {
                                                color: '#202124',
                                                fontWeight: 700,
                                                fontSize: '1rem',
                                            },
                                            '& .MuiPickersArrowSwitcher-button': {
                                                color: '#1a73e8',
                                                '&:hover': {
                                                    background: 'rgba(26, 115, 232, 0.15)',
                                                }
                                            },
                                            '& .MuiDayCalendar-weekDayLabel': {
                                                color: '#5f6368',
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                            },
                                            '& .MuiDayCalendar-header': {
                                                background: 'transparent',
                                                paddingTop: '8px',
                                            },
                                            '& .MuiPickersLayout-root': {
                                                background: '#e8eef3',
                                            }
                                        }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <StyledInputLabel htmlFor="OriginalReleasedate">Original Release Date</StyledInputLabel>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                format="DD/MM/YYYY"
                                value={releaseInfo.ogn_release_date ? moment(releaseInfo.ogn_release_date) : null}
                                onChange={date => {
                                    setReleaseInfo({ ...releaseInfo, ogn_release_date: date && date.valueOf() || null })
                                }}
                                enableAccessibleFieldDOMStructure={false}
                                slots={{
                                    textField: (params) => (
                                        <CssTextField
                                            {...params}
                                            size="small"
                                            fullWidth
                                            placeholder="Select original release date"
                                            inputProps={{
                                                ...params.inputProps,
                                            }}
                                            InputProps={{
                                                ...params.InputProps,
                                            }}
                                        />
                                    )
                                }}
                                slotProps={{
                                    openPickerIcon: {
                                        sx: {
                                            color: '#1a73e8',
                                        }
                                    },
                                    popper: {
                                        sx: {
                                            '& .MuiPaper-root': {
                                                borderRadius: '12px',
                                                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.3)',
                                                background: '#e8eef3',
                                                border: '1px solid rgba(0, 0, 0, 0.2)',
                                            },
                                            '& .MuiPickersDay-root': {
                                                color: '#202124',
                                                fontWeight: 500,
                                                '&:hover': {
                                                    background: 'rgba(26, 115, 232, 0.2)',
                                                },
                                                '&.Mui-selected': {
                                                    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                                                    color: '#ffffff',
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
                                                    }
                                                }
                                            },
                                            '& .MuiPickersYear-yearButton': {
                                                color: '#202124',
                                                fontWeight: 500,
                                                fontSize: '1rem',
                                                '&:hover': {
                                                    background: 'rgba(26, 115, 232, 0.2)',
                                                },
                                                '&.Mui-selected': {
                                                    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                                                    color: '#ffffff',
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
                                                    }
                                                },
                                            },
                                            '& .MuiYearCalendar-root': {
                                                color: '#202124',
                                            },
                                            '& .MuiPickersCalendarHeader-root': {
                                                color: '#202124',
                                                background: 'linear-gradient(135deg, rgba(26, 115, 232, 0.15) 0%, rgba(0, 191, 165, 0.15) 100%)',
                                                padding: '16px',
                                                marginBottom: '8px',
                                            },
                                            '& .MuiPickersCalendarHeader-label': {
                                                color: '#202124',
                                                fontWeight: 700,
                                                fontSize: '1rem',
                                            },
                                            '& .MuiPickersArrowSwitcher-button': {
                                                color: '#1a73e8',
                                                '&:hover': {
                                                    background: 'rgba(26, 115, 232, 0.15)',
                                                }
                                            },
                                            '& .MuiDayCalendar-weekDayLabel': {
                                                color: '#5f6368',
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                            },
                                            '& .MuiDayCalendar-header': {
                                                background: 'transparent',
                                                paddingTop: '8px',
                                            },
                                            '& .MuiPickersLayout-root': {
                                                background: '#e8eef3',
                                            }
                                        }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    {/* UPC/EAN */}
                    <Grid item xs={12} md={6}>
                        <StyledInputLabel>UPC/EAN (Optional)</StyledInputLabel>
                        <CssTextField
                            variant="outlined"
                            fullWidth
                            size="small"
                            placeholder="Enter UPC/EAN code (numbers only)"
                            value={releaseInfo.upc_ean}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                setReleaseInfo({ ...releaseInfo, upc_ean: value });
                            }}
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*'
                            }}
                        />
                    </Grid>

                    {/* P-Line, Courtesy Line & C-Line */}
                    <Grid item xs={12} md={6}>
                        <StyledInputLabel>P-Line<span className="required-span">*</span></StyledInputLabel>
                        <CssTextField
                            variant="outlined"
                            fullWidth
                            size="small"
                            placeholder={`℗ ${new Date().getFullYear()} Phonographic Copyright Line`}
                            value={releaseInfo.p_line}
                            onChange={(e) => setReleaseInfo({ ...releaseInfo, p_line: e.target.value })}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <StyledInputLabel sx={{ mb: 0 }}>Courtesy Line</StyledInputLabel>
                            <Tooltip
                                title="Use this field for display purposes next to the P and C line text. Usually this is something like the record label."
                                placement="top"
                                arrow
                            >
                                <InfoOutlinedIcon sx={{ fontSize: '1.25rem', color: '#1a73e8', cursor: 'help' }} />
                            </Tooltip>
                        </Box>
                        <CssTextField
                            variant="outlined"
                            fullWidth
                            size="small"
                            placeholder="Courtesy of Your Label Name"
                            value={releaseInfo.cour_line}
                            onChange={(e) => setReleaseInfo({ ...releaseInfo, cour_line: e.target.value })}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <StyledInputLabel>C-Line<span className="required-span">*</span></StyledInputLabel>
                        <CssTextField
                            variant="outlined"
                            fullWidth
                            size="small"
                            placeholder={`© ${new Date().getFullYear()} Copyright Line`}
                            value={releaseInfo.c_line}
                            onChange={(e) => setReleaseInfo({ ...releaseInfo, c_line: e.target.value })}
                        />
                    </Grid>
                    {/* Additional Information - Full Width */}
                    <Grid item xs={12}>
                        <StyledInputLabel>Additional Information & Requests (If Any)</StyledInputLabel>
                        <CssTextField
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="Enter any additional information or special requests here..."
                            value={releaseInfo.add_info}
                            onChange={(e) => setReleaseInfo({ ...releaseInfo, add_info: e.target.value })}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" mt={3}>
                            <StyledButton onClick={createReleaseInfo} startIcon={<SaveIcon />}>
                                {editMode ? 'Update Release Info' : 'Save & Continue'}
                            </StyledButton>
                        </Box>
                    </Grid>
                </Grid>
            </Grid>

            <CustomShowModal
                open={open}
                handleClose={() => setOpen(false)}
                maxWidth="sm"
                title="Artwork Guidelines"
            >
                <Box sx={{ py: 1 }}>
                    {artworkGuidlines.map((item, key) => {
                        return (
                            <Box
                                key={key}
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    mb: 1.5,
                                    py: key === 0 ? 1.5 : 1,
                                    px: key === 0 ? 1.5 : 0,
                                    background: key === 0 ? 'linear-gradient(135deg, rgba(26, 115, 232, 0.08) 0%, rgba(0, 191, 165, 0.08) 100%)' : 'transparent',
                                    borderRadius: key === 0 ? '6px' : '0',
                                    borderLeft: key !== 0 ? '2px solid #1a73e8' : 'none',
                                    pl: key !== 0 ? 1.5 : 0,
                                }}
                            >
                                {key !== 0 && (
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontWeight: 700,
                                            marginRight: 1,
                                            color: '#1a73e8',
                                            minWidth: '18px',
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        {key}.
                                    </Typography>
                                )}
                                <Typography
                                    sx={{
                                        color: '#202124',
                                        lineHeight: 1.5,
                                        fontWeight: key === 0 ? 600 : 400,
                                        fontSize: key === 0 ? '0.9rem' : '0.85rem'
                                    }}
                                >
                                    {item}
                                </Typography>
                            </Box>
                        );
                    })}
                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            background: 'linear-gradient(135deg, rgba(239, 83, 80, 0.08) 0%, rgba(229, 57, 53, 0.08) 100%)',
                            borderRadius: '6px',
                            borderLeft: '3px solid #ef5350',
                        }}
                    >
                        <Typography sx={{ color: '#202124', fontWeight: 600, fontSize: '0.8rem' }}>
                            ⚠️ Important: Do not send thumbnails, .png files, or smaller images.
                        </Typography>
                    </Box>
                </Box>
            </CustomShowModal>
        </Grid >
    )
}

export default ReleaseInfo
